import { WarehouseOpsSpecialMoveDtoCreate } from 'src/app/api/models';

// Operazioni dedicate che portano un asset con seriale verso o da le sedi di sistema del magazzino
// (guasti, RAEE, smarriti). Le regole rispecchiano quelle del backend (planSpecialMove): il backend
// resta l'autorità, qui servono solo a mostrare le azioni che hanno senso per l'asset.
// Le conversioni tra giacenza di vendita e di manutenzione sono nel client generato dal backend che le
// supporta: l'unione resta valida anche quando il client include già i due valori.
export type SpecialOperation =
  | WarehouseOpsSpecialMoveDtoCreate['operation']
  | 'CONVERT_TO_MAINTENANCE'
  | 'CONVERT_TO_SALE';

export const SPECIAL_MOVE_GRANT = 'WAREHOUSE-MOVEMENTS-SPECIAL.C';
// Smaltimento dalla lista RAEE: uscita definitiva, permesso separato da quello dei movimenti verso le sedi di sistema
export const SPECIAL_DISPOSE_GRANT = 'WAREHOUSE-MOVEMENTS-DISPOSE.C';

export type SpecialFunction = 'GUASTI' | 'RAEE' | 'SMARRITI';

// Dove si trova l'asset rispetto alle sedi di sistema
export type SpecialPosition = 'WAREHOUSE' | SpecialFunction | 'CUSTOMER' | 'NONE';

export interface SpecialMoveAssetLike {
  active?: boolean;
  repair_on_center?: boolean;
  lost?: boolean;
  // 1 = vendita, 2 = manutenzione
  purpose?: number;
  registry_end_customer?: unknown;
  related_warehouse?: { name?: string; special_function?: string | null } | null;
}

export interface SpecialMoveOperationInfo {
  label: string;
  icon: string;
  title: string;
  description: string;
  // Senza ritorno dall'applicazione: dalla lista RAEE l'asset esce solo con lo smaltimento
  irreversible?: boolean;
  // Ripristino e ritrovamento in magazzino possono indicare un magazzino diverso da quello di origine
  askDestination?: boolean;
}

export const SPECIAL_MOVE_OPERATIONS: Record<SpecialOperation, SpecialMoveOperationInfo> = {
  REPORT_FAULTY: {
    label: 'Segnala guasto',
    icon: 'pi pi-exclamation-triangle',
    title: 'Segnala guasto',
    description:
      "L'asset passa dal magazzino alla lista guasti dello stesso magazzino e viene segnato come guasto. Esce dalla giacenza disponibile.",
  },
  RESTORE_FAULTY: {
    label: 'Ripristina da guasto',
    icon: 'pi pi-undo',
    title: 'Ripristina da guasto',
    description:
      "L'asset torna in magazzino e non è più segnato come guasto. Rientra nella giacenza disponibile.",
    askDestination: true,
  },
  SEND_TO_RAEE: {
    label: 'Da smaltire (RAEE)',
    icon: 'pi pi-trash',
    title: 'Invia a RAEE (da smaltire)',
    description:
      "L'asset passa alla lista RAEE del magazzino, dove resta in attesa di smaltimento. Esce dalla giacenza disponibile.",
    irreversible: true,
  },
  REPORT_LOST: {
    label: 'Segnala smarrito',
    icon: 'pi pi-question-circle',
    title: 'Segnala smarrito',
    description:
      "In magazzino l'asset passa alla lista smarriti e viene segnato come smarrito. Se è presso il cliente cambia solo lo stato (smarrito dal cliente): la giacenza non cambia, perché il pezzo era già uscito.",
  },
  FOUND: {
    label: 'Ritrovato',
    icon: 'pi pi-check-circle',
    title: 'Segna come ritrovato',
    description:
      "L'asset smarrito torna in magazzino (o, se era presso il cliente, non è più segnato come smarrito).",
    askDestination: true,
  },
  CONVERT_TO_MAINTENANCE: {
    label: 'Sposta in manutenzione',
    icon: 'pi pi-wrench',
    title: 'Sposta in manutenzione',
    description:
      "L'asset passa dalla giacenza di vendita a quella di manutenzione dello stesso magazzino e diventa un asset per manutenzione. Se è presso il cliente cambia solo l'utilizzo: la giacenza non cambia.",
  },
  CONVERT_TO_SALE: {
    label: 'Sposta in vendita',
    icon: 'pi pi-shopping-cart',
    title: 'Sposta in vendita',
    description:
      "L'asset passa dalla giacenza di manutenzione a quella di vendita dello stesso magazzino e non è più un asset per manutenzione. Se è presso il cliente cambia solo l'utilizzo: la giacenza non cambia.",
  },
};

export function specialPosition(asset: SpecialMoveAssetLike): SpecialPosition {
  const office = asset.related_warehouse;
  if (office) {
    const fn = office.special_function;
    return fn === 'GUASTI' || fn === 'RAEE' || fn === 'SMARRITI' ? fn : 'WAREHOUSE';
  }
  return asset.registry_end_customer ? 'CUSTOMER' : 'NONE';
}

// Azioni disponibili per un asset, nell'ordine in cui compaiono nel menu
export function specialMoveOperationsFor(asset: SpecialMoveAssetLike): SpecialOperation[] {
  if (asset.active === false || asset.repair_on_center) return [];
  // conversione verso l'utilizzo opposto a quello attuale (senza utilizzo noto: verso manutenzione)
  const convert: SpecialOperation =
    asset.purpose === 2 ? 'CONVERT_TO_SALE' : 'CONVERT_TO_MAINTENANCE';
  switch (specialPosition(asset)) {
    case 'WAREHOUSE':
      // "Da smaltire" è irreversibile: sempre ultima voce
      return asset.lost
        ? ['REPORT_FAULTY', convert, 'SEND_TO_RAEE']
        : ['REPORT_FAULTY', 'REPORT_LOST', convert, 'SEND_TO_RAEE'];
    case 'GUASTI':
      return ['RESTORE_FAULTY', convert, 'SEND_TO_RAEE'];
    case 'SMARRITI':
      return ['FOUND'];
    case 'CUSTOMER':
      return asset.lost ? ['FOUND', convert] : ['REPORT_LOST', convert];
    default:
      return [];
  }
}

// Il ritrovamento presso il cliente non ha destinazione: è solo uno stato
export function specialMoveAsksDestination(
  operation: SpecialOperation,
  assets: SpecialMoveAssetLike[]
): boolean {
  return (
    !!SPECIAL_MOVE_OPERATIONS[operation].askDestination &&
    assets.some(a => specialPosition(a) !== 'CUSTOMER')
  );
}

export function specialFunctionLabel(fn?: string | null): string | null {
  switch (fn) {
    case 'GUASTI':
      return 'Guasti';
    case 'RAEE':
      return 'RAEE';
    case 'SMARRITI':
      return 'Smarriti';
    default:
      return null;
  }
}

// Motivo di un movimento interno verso o da le sedi di sistema (WarehouseMovement.special_reason)
export function movementReasonLabel(reason?: string | null): string | null {
  switch (reason) {
    case 'REPORT_FAULTY':
      return 'Segnalazione guasto';
    case 'RESTORE_FAULTY':
      return 'Ripristino da guasto';
    case 'SEND_TO_RAEE':
      return 'Invio a RAEE';
    case 'REPORT_LOST':
      return 'Segnalazione smarrimento';
    case 'FOUND':
      return 'Ritrovamento';
    case 'CONVERT_TO_MAINTENANCE':
      return 'Conversione in manutenzione';
    case 'CONVERT_TO_SALE':
      return 'Conversione in vendita';
    case 'DISPOSE_RAEE':
      return 'Smaltimento RAEE';
    default:
      return null;
  }
}

// Dove si trova l'asset, in parole (per l'elenco della modale)
export function specialPositionLabel(asset: SpecialMoveAssetLike): string {
  const office = asset.related_warehouse;
  switch (specialPosition(asset)) {
    case 'CUSTOMER':
      return asset.lost ? 'Presso il cliente (smarrito)' : 'Presso il cliente';
    case 'NONE':
      return 'Fuori magazzino';
    default:
      return office?.name ?? '-';
  }
}
